import { useCallback, useState } from "react";

import store, { type StoreBase } from "store2";

import { captureException } from "src/lib/monitoring";

import type { UnsavedValue } from "./use-unsaved-value";

type StorageOperation = "read" | "write" | "remove";

/**
 * Which browser storage a key lives in. `local` persists until evicted;
 * `session` is scoped to the tab and dies with it — the right home for state
 * that shouldn't outlive the working session (e.g. an in-progress draft).
 */
export type StorageLocation = "local" | "session";

const STORAGE_LOCATIONS: Record<StorageLocation, StoreBase> = {
  local: store,
  session: store.session,
};

const getStorageKeyBucket = (key: string): string => {
  const segments = key.split(":");
  if (segments.length >= 2) {
    return segments.slice(1, 3).join(":") || "_misc";
  }
  return "_misc";
};

// A full localStorage keeps throwing on every write, and `useRecentlyViewed`
// writes on every navigation — so without deduping we'd emit one Sentry/Datadog
// event per route change. Reporting once per (operation, bucket) for the tab
// session keeps the signal without the flood. The bucket already drops the
// workspace id, so distinct workspaces collapse to one entry.
const reportedStorageFailures = new Set<string>();

// Exported only so tests can reset the once-per-session dedupe set between cases.
export const resetReportedStorageFailures = (): void => {
  reportedStorageFailures.clear();
};

const reportStorageFailure = (
  operation: StorageOperation,
  key: string,
  location: StorageLocation,
  err: unknown,
) => {
  const bucket = getStorageKeyBucket(key);
  const dedupeKey = `${operation}:${bucket}`;
  if (reportedStorageFailures.has(dedupeKey)) {
    return;
  }
  reportedStorageFailures.add(dedupeKey);

  captureException(
    err,
    {
      level: "warning",
      tags: {
        // Tag names kept as-is so existing Sentry queries keep resolving, even
        // though these now cover session storage too; `storage_location`
        // splits them.
        local_storage_operation: operation,
        local_storage_key_bucket: bucket,
        storage_location: location,
      },
    },
    {
      source: "local-storage",
      component: "usePersistedState",
      errorType: "storage",
    },
  );
};

// Exported so hooks with their own persistence shape (e.g. a ref-backed draft
// writer) reuse this try/catch + deduped reporting instead of reimplementing it
// around `store2`. `location` defaults to local storage, matching
// `usePersistedState`.
export const getStoredValue = <TValue>(
  key: string,
  initialValue: TValue,
  location: StorageLocation = "local",
): TValue => {
  try {
    const stored = STORAGE_LOCATIONS[location].get(key);
    return stored == undefined ? initialValue : stored;
  } catch (err) {
    reportStorageFailure("read", key, location, err);
    console.warn("Failed to read `%s` from %s storage", key, location, err);
    return initialValue;
  }
};

// Exported so a caller that has to scan storage (a one-time migration off a
// key format, say) gets the same try/catch and deduped reporting as the
// single-key helpers instead of touching `store2` directly.
export const getStoredKeys = (
  location: StorageLocation = "local",
): string[] => {
  try {
    return STORAGE_LOCATIONS[location].keys();
  } catch (err) {
    reportStorageFailure("read", "*", location, err);
    console.warn("Failed to list keys in %s storage", location, err);
    return [];
  }
};

export const setStoredValue = <TValue>(
  key: string,
  value: TValue,
  location: StorageLocation = "local",
) => {
  try {
    STORAGE_LOCATIONS[location].set(key, value);
  } catch (err) {
    reportStorageFailure("write", key, location, err);
    console.warn("Failed to persist `%s` to %s storage", key, location, err);
  }
};

export const removeStoredValue = (
  key: string,
  location: StorageLocation = "local",
) => {
  try {
    STORAGE_LOCATIONS[location].remove(key);
  } catch (err) {
    reportStorageFailure("remove", key, location, err);
    console.warn("Failed to remove `%s` from %s storage", key, location, err);
  }
};

export const usePersistedState = <TValue>(
  key: string,
  initialValue: TValue,
): UnsavedValue<TValue> => {
  // Lazy initializer reads from storage synchronously on first render so
  // callers never observe `undefined` before falling back to `initialValue`.
  // The previous `useState(store.get(key)) + useEffect(setValue(...))` pattern
  // caused a visible flash (for example `PageSidebar` rendering collapsed at
  // 40px, then expanding to 280px after the effect ran — a 240px layout shift
  // on every cold load).
  const [value, setValue] = useState<TValue | undefined>(() => {
    return getStoredValue(key, initialValue);
  });

  const set = useCallback(
    (value: TValue) => {
      setValue(value);
      setStoredValue(key, value);
    },
    [key],
  );

  const restore = useCallback(() => {
    setValue(undefined);
  }, []);

  const clear = useCallback(() => {
    setValue(undefined);
    removeStoredValue(key);
  }, [key]);

  return { value, set, restore, clear };
};
